import { useEffect, useMemo, useState } from 'react';
import { Alert, Collapse, Modal, Segmented, Select, Space, Typography } from 'antd';

import FormulaEditor, { FormulaReference } from '../common/FormulaEditor';
import { InputDate, InputMeasurement, InputNum, InputSelect, InputText, InputTime } from '../Input';
import { TRANSFORM_LABELS } from './DrivenFromModal';
import { transformInfo } from '../../config/elementCoercion';
import {
	analyzeFormula,
	buildFormulaTokens,
	entryScopeOf,
	formulaElementResolver,
	mergedElementsForCard,
	previewFormula,
} from '../../utils/formulaTokens';
import { normalizeId } from '../../utils/ids';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';

/**
 * Configures ONE calculated element: "this element's value is computed from
 * other elements on the same record" — `options.drivenFrom` with
 * `kind: 'formula'`. The API re-validates on save and materializes the
 * managed rule; this modal exists so the author hears "no, and here is why"
 * before the round trip, and sees the value the formula would produce.
 *
 * The editor is the platform's formula editor (common/FormulaEditor, which
 * the chart builder's calculations share): element references are pills
 * (`{{volume-a}}` is unambiguous where `volume-a + …` would read as four
 * operands), operators and functions are typed, and a function picker drops
 * `round()` at the caret with the caret parked inside the parens.
 *
 * Validation runs on every change through the SAME module the API runs
 * (utils/formula, vendored byte-identical), against the card's merged
 * elements (inherited + the form's unsaved own elements) and the target
 * element's type, via the coercion mirror. A preview evaluates the formula
 * over sample values so "what will this actually produce?" is answered here.
 *
 * A dot-walk reads another record; the callout for that is REQUIRED, not
 * decorative — a reader of this card sees data derived from records they may
 * not be able to open (the driven-field write-through's read-side twin).
 */

const BLANKS_OPTIONS = [
	{ value: 'zero', label: 'Count as zero' },
	{ value: 'empty', label: 'Leave the result blank' },
];

const NOW_SAMPLE = () => new Date();

const SAMPLE_TERMINALS = {
	createdAt: { name: 'createdAt', dataType: 'date-time', label: { singular: 'Created' }, options: {} },
	updatedAt: { name: 'updatedAt', dataType: 'date-time', label: { singular: 'Updated' }, options: {} },
};

function sampleInput(element, value, onChange) {
	const common = { elementName: element.name, elementLabel: element.label?.singular || element.name, value, onChange };
	const choices = Array.isArray(element.options?.choices) ? element.options.choices : [];
	switch (element.dataType) {
		case 'number': case 'decimal': case 'integer': case 'rating':
			return <InputNum {...common} dataType={element.dataType === 'rating' ? 'number' : element.dataType} />;
		case 'date':
			return <InputDate {...common} defaultToday={false} />;
		case 'date-time':
			return <InputDate {...common} showTime={true} defaultToday={false} />;
		// A `time` element is its own type to the language now, so the sample
		// input has to commit a real 'HH:mm' — an InputText here would let a
		// preview be typed that the formula can only warn-and-blank on.
		case 'time':
			return <InputTime {...common} />;
		case 'measurement':
			return <InputMeasurement {...common} element={element} />;
		case 'choice':
			return choices.length
				? <InputSelect {...common} options={choices} />
				: <InputText {...common} />;
		case 'card-lookup':
			return null;
		default:
			return <InputText {...common} />;
	}
}

/**
 * `entry` (KPR-686): the element is a COLUMN of a repeating group, and its
 * formula is worked out for every entry — `{ group, columns }`, the group as
 * the editor holds it and its columns (each under the name the API will give
 * it). Its formula reads the entry's other columns first, then the item's own
 * elements (`siblingElements`, the card's), and follows no lookup.
 */
export default function FormulaModal({ open, onCancel, onSave, element, targetCard, collectionCards = [], siblingElements = [], entry = null }) {

	const cardDefinitions = useOptionalCardDefinitions();
	// Last-wins over the app cache, falling back per card to the cards this
	// form was handed (the cache may still be loading).
	const cardsById = cardDefinitions?.cardsById;
	const elementSetsById = cardDefinitions?.elementSetsById;
	const getElementsForCard = useMemo(() => formulaElementResolver({ cardsById, elementSetsById }, collectionCards), [cardsById, elementSetsById, collectionCards]);
	const cacheLoading = !!cardDefinitions?.loading;

	const initialConfig = element?.options?.drivenFrom;

	const [formulaText, setFormulaText] = useState('');
	const [blanks, setBlanks] = useState('zero');
	const [transformType, setTransformType] = useState('');
	const [samples, setSamples] = useState({});

	// Opening (re)seeds from the stored config — Cancel must discard edits.
	useEffect(() => {
		if (!open) return;
		const stored = initialConfig?.kind === 'formula' ? initialConfig : null;
		setFormulaText(stored?.formula || '');
		setBlanks(stored?.blanks === 'empty' ? 'empty' : 'zero');
		setTransformType(stored?.transform?.type || '');
		setSamples({});
	}, [open, initialConfig]);

	// The card's merged elements: what the parent chain contributes plus the
	// form's own (unsaved) elements — the set the saved card will carry.
	const mergedElements = useMemo(() => mergedElementsForCard({
		ownElements: siblingElements,
		parentCardId: targetCard?.parentCardId,
		getElementsForCard,
	}), [siblingElements, targetCard?.parentCardId, getElementsForCard]);

	// A COLUMN's formula (KPR-686) is read in one entry's scope: the group's
	// other columns first, then the item's elements; no lookup is followed.
	const entryGroup = entry?.group || null;
	const entryColumns = entry?.columns || null;
	const scope = useMemo(
		() => (entryGroup ? entryScopeOf(entryGroup, entryColumns, { excludeName: element?.name }) : null),
		[entryGroup, entryColumns, element?.name]
	);
	const groupLabel = entryGroup?.label?.singular || entryGroup?.name || 'entry';

	const tokens = useMemo(
		() => buildFormulaTokens(mergedElements, getElementsForCard, scope
			? { walks: false, groups: true, entry: { columns: entryColumns, label: groupLabel.toLowerCase(), excludeName: element?.name } }
			: { excludeName: element?.name, groups: true }),
		[mergedElements, getElementsForCard, element?.name, scope, entryColumns, groupLabel]
	);

	const analysis = useMemo(
		() => analyzeFormula(formulaText, { mergedElements, getElementsForCard, targetElement: element, entry: scope }),
		[formulaText, mergedElements, getElementsForCard, element, scope]
	);

	// Keep the transform legal for the current result: a single legal one is
	// chosen; a stale one is replaced rather than silently saved.
	const legalTransforms = analysis.ok ? (analysis.target.transforms || ['none']) : [];
	useEffect(() => {
		if (!analysis.ok) return; // an unfinished formula must not discard a seeded transform
		setTransformType(prev => {
			if (prev && legalTransforms.includes(prev)) return prev;
			return legalTransforms.length ? legalTransforms[0] : '';
		});
	}, [analysis.ok, legalTransforms.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

	const chosenTransform = transformType ? transformInfo(transformType) : null;

	const byName = useMemo(() => new Map(mergedElements.map(el => [el.name, el])), [mergedElements]);
	const columnByName = useMemo(() => new Map((entryColumns || []).filter(c => c?.name).map(c => [c.name, c])), [entryColumns]);
	// The record's own timestamps are inputs too: a formula on {{createdAt}}
	// is not "the same for every record". A group (KPR-686) has no sample
	// input — its entries read as none in this preview — so it is left out.
	const sampleElements = useMemo(
		() => (analysis.ok ? analysis.ownRefs.map(name => byName.get(name) || SAMPLE_TERMINALS[name]).filter(el => el && el.dataType !== 'repeating-group') : []),
		[analysis, byName]
	);
	const sampleColumns = useMemo(
		() => (analysis.ok && scope ? (analysis.columnRefs || []).map(name => columnByName.get(name)).filter(Boolean) : []),
		[analysis, scope, columnByName]
	);
	const readsGroups = analysis.ok && analysis.ownRefs.some(name => byName.get(name)?.dataType === 'repeating-group');
	const [entrySamples, setEntrySamples] = useState({});
	useEffect(() => { if (open) setEntrySamples({}); }, [open]);

	const preview = useMemo(() => {
		if (!analysis.ok) return null;
		return previewFormula(formulaText, {
			mergedElements,
			getElementsForCard,
			targetElement: element,
			values: samples,
			blanks,
			now: NOW_SAMPLE(),
			entry: scope,
			entryValues: entrySamples,
		});
	}, [analysis.ok, formulaText, mergedElements, getElementsForCard, element, samples, blanks, scope, entrySamples]);

	const walkCardNames = useMemo(() => analysis.ok
		? analysis.walks.map(via => {
			const lookup = byName.get(via);
			const card = (collectionCards || []).find(c => normalizeId(c?._id) === normalizeId(lookup?.options?.lookupCardId));
			return card?.name || lookup?.label?.singular || via;
		})
		: [], [analysis, byName, collectionCards]);

	const complete = analysis.ok && !!transformType;

	const handleSave = () => {
		if (!complete) return;
		onSave({
			kind: 'formula',
			formula: analysis.canonical,
			blanks,
			transform: { type: transformType },
		});
	};

	const statusAlert = (() => {
		if (analysis.empty) return null;
		if (!analysis.ok && cacheLoading) {
			// A reference the cache cannot resolve YET is not an error; say so
			// rather than flashing red under a stored, valid formula.
			return <Alert type='info' showIcon message='Loading card definitions…' />;
		}
		if (!analysis.ok) {
			return (
				<Alert
					type='error'
					showIcon
					message={analysis.error?.message || 'This formula is not valid yet.'}
					description={Number.isInteger(analysis.error?.position) ? `Near character ${analysis.error.position + 1}.` : null}
				/>
			);
		}
		const targetLabel = element?.label?.singular || 'this element';
		const conversion = transformType && transformType !== 'none' ? ` — ${TRANSFORM_LABELS[transformType] || transformType}` : '';
		return (
			<Alert
				type='success'
				showIcon
				message={`Result: ${analysis.typeLabel}, stored in ${targetLabel}${conversion}.`}
				description={analysis.target.note || null}
			/>
		);
	})();

	return (
		<Modal
			open={open}
			onCancel={onCancel}
			onOk={handleSave}
			okText='Save'
			okButtonProps={{ disabled: !complete }}
			title={`Calculated — ${element?.label?.singular || (scope ? 'column' : 'element')}`}
			width={760}
			destroyOnClose
		>
			<Space direction='vertical' size={12} style={{ width: '100%' }}>

				{scope ? (
					<Typography.Text type='secondary' data-testid='formula-entry-scope'>
						This column is worked out for every {groupLabel.toLowerCase()}, from that {groupLabel.toLowerCase()}&rsquo;s
						other columns and the item&rsquo;s own fields. Nobody fills it in by hand; it updates whenever the
						item is saved.
					</Typography.Text>
				) : (
					<Typography.Text type='secondary'>
						This field's value is worked out from other fields on the same record. Nobody
						fills it in by hand; it updates whenever the record is saved.
					</Typography.Text>
				)}

				<div>
					<div className='d-block'><Typography.Text strong>Formula</Typography.Text></div>
					<FormulaEditor
						value={formulaText}
						onChange={setFormulaText}
						tokens={tokens}
						ariaLabel='Formula'
						placeholder='Pick elements from the menu and combine them, such as adding two volumes'
						multiline
						pickerId='formulaFunctionPicker'
						hint={<>Pick fields with the <code>{'{{ }}'}</code> button; type operators between them.</>}
					/>
				</div>

				{statusAlert}

				{analysis.ok && legalTransforms.length > 1 ? (
					<div>
						<label htmlFor='formulaTransform' className='d-block'>Conversion</label>
						<Select
							id='formulaTransform'
							size='large'
							style={{ width: '100%' }}
							value={transformType || undefined}
							onChange={value => setTransformType(value)}
							options={legalTransforms.map(name => ({ value: name, label: TRANSFORM_LABELS[name] || name }))}
						/>
					</div>
				) : null}

				{chosenTransform?.lossy ? <Alert type='warning' showIcon message={chosenTransform.warning} /> : null}

				<div>
					<div className='d-block'><Typography.Text strong>When an input is blank</Typography.Text></div>
					<Segmented
						aria-label='When an input is blank'
						block
						value={blanks}
						onChange={value => setBlanks(value)}
						options={BLANKS_OPTIONS}
					/>
					<Typography.Text type='secondary'>
						Sums and averages skip blanks either way; this decides what <code>+</code>, <code>−</code>, <code>×</code> and <code>÷</code> see.
					</Typography.Text>
				</div>

				{analysis.ok && walkCardNames.length ? (
					<Alert
						type='warning'
						showIcon
						message={`Anyone who can read ${targetCard?.name || 'this card'} records sees values derived from ${walkCardNames.join(' and ')} records — including records they can't open.`}
					/>
				) : null}

				{analysis.ok && analysis.timeDependent ? (
					<Alert
						type='info'
						showIcon
						message='This formula reads the clock. The value refreshes once a day and whenever the record is saved.'
					/>
				) : null}

				{analysis.ok ? (
					<Collapse
						size='small'
						defaultActiveKey={sampleElements.length || sampleColumns.length ? ['try'] : []}
						items={[{
							key: 'try',
							label: 'Try it',
							children: (
								<div className='formula-modal__try'>
									{sampleColumns.length || sampleElements.length ? (
										<div className='formula-modal__samples'>
											{sampleColumns.map(el => {
												const input = sampleInput(el, entrySamples[el.name], (name, value) => setEntrySamples(prev => ({ ...prev, [name]: value })));
												return input ? <div key={`entry:${el.name}`} className='formula-modal__sample'>{input}</div> : null;
											})}
											{sampleElements.map(el => {
												const input = sampleInput(el, samples[el.name], (name, value) => setSamples(prev => ({ ...prev, [name]: value })));
												return input ? <div key={el.name} className='formula-modal__sample'>{input}</div> : null;
											})}
										</div>
									) : readsGroups ? null : (
										<Typography.Text type='secondary'>This formula reads no fields of this record; the result is the same for every record.</Typography.Text>
									)}
									{readsGroups ? (
										<Typography.Text type='secondary' className='d-block' data-testid='formula-preview-groups'>
											A repeating group reads as having no entries in this preview.
										</Typography.Text>
									) : null}
									{analysis.walks.length ? (
										<Typography.Text type='secondary' className='d-block'>
											Linked records ({analysis.walks.join(', ')}) read as blank in this preview.
										</Typography.Text>
									) : null}
									<div className='formula-modal__result' aria-live='polite'>
										<span className='formula-modal__result-label'>Result</span>
										<span className='formula-modal__result-value' data-testid='formula-preview'>{preview?.text || '—'}</span>
									</div>
									{preview?.warnings?.length ? (
										<Typography.Text type='warning'>{[...new Set(preview.warnings)].join(' · ')}</Typography.Text>
									) : null}
								</div>
							),
						}]}
					/>
				) : null}

				<Collapse
					size='small'
					items={[{ key: 'reference', label: 'Formula reference', children: <FormulaReference /> }]}
				/>

			</Space>
		</Modal>
	);
}
